import { customElement, property, state } from "lit/decorators.js";
import { html, LitElement } from "lit";

// Define the type for the items in the menu
type MenuItem =
  | string
  | {
      label: string;
      color?: string;
      url?: string;
      onClick?: () => void;
    };

@customElement("floating-popover-menu")
export class FloatingPopoverMenu extends LitElement {
  @property({ type: Array })
  items: MenuItem[] = []; // Use the defined MenuItem type

  @property({ type: String })
  menuPosition: "top" | "bottom" | "left" | "right" = "bottom";

  @property({ type: String })
  placeholder = ""; // Optional placeholder for the button

  @property({ type: Object })
  triggerElement: HTMLElement | null = null; // Allows passing any element as the trigger

  @state()
  public isOpen = false;

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.handleInternalClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.handleInternalClick);
    document.removeEventListener("click", this.closeMenuOnClickOutside);
  }

  private handleInternalClick(event: Event) {
    event.stopPropagation();
  }

  openMenu() {
    this.isOpen = true;
    document.addEventListener("click", this.closeMenuOnClickOutside);
  }

  closeMenu() {
    this.isOpen = false;
    document.removeEventListener("click", this.closeMenuOnClickOutside);
  }

  closeMenuOnClickOutside = (event: MouseEvent) => {
    if (!this.shadowRoot?.contains(event.target as Node)) {
      this.closeMenu();
    }
  };

  render() {
    return html`
      <div class="floating-menu-wrapper">
        <!-- Render custom trigger element or default button -->
        ${this.triggerElement
          ? html`
              <div @click="${this.openMenu}">
                <slot name="trigger">${this.triggerElement}</slot>
              </div>
            `
          : html`
              <button class="btn" @click="${this.openMenu}">
                ${this.placeholder || "Open Menu"}
              </button>
            `}

        <div
          class="floating-menu${this.isOpen ? " is-open" : ""} ${this.menuPosition}"
        >
          ${this.items.map((item) =>
            typeof item === "string"
              ? html`
                  <div
                    class="floating-menu-item"
                    @click="${() => {
                      this.dispatchEvent(
                        new CustomEvent("item-click", {
                          detail: { item },
                          bubbles: true,
                          composed: true,
                        })
                      );
                      this.closeMenu();
                    }}"
                  >
                    ${item}
                  </div>
                `
              : html`
                  <div
                    class="floating-menu-item"
                    @click="${() => {
                      // Null check for item.onClick
                      if (item?.onClick) {
                        item.onClick();
                      }
                      this.dispatchEvent(
                        new CustomEvent("item-click", {
                          detail: { item },
                          bubbles: true,
                          composed: true,
                        })
                      );
                      this.closeMenu();
                    }}"
                  >
                    ${item?.color
                      ? html`<div
                          class="color-box"
                          style="background: ${item.color};"
                        ></div>`
                      : ""}
                    ${item?.label || item}
                  </div>
                `
          )}
        </div>
      </div>
    `;
  }
}
